import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { comics } from '@/mocks/comics';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const comic = comics.find((c) => c.id === id);

  if (!comic) {
    return <NotFound />;
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Article',
        headline: `${comic.title} - 韩漫网站`,
        description: comic.description,
        author: {
          '@type': 'Person',
          name: comic.author,
        },
        datePublished: '2026-01-01',
        dateModified: comic.chapters[0]?.updatedAt || '2026-08-01',
        url: `https://www.hanman.site/comic/${comic.id}`,
        inLanguage: 'zh-CN',
        about: {
          '@type': 'Thing',
          name: `韩国漫画 ${comic.title}`,
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: 'https://www.hanman.site',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: comic.title,
          },
        ],
      },
    ],
  };

  return (
    <Layout>
      <SEOHead
        title={`${comic.title} - ${comic.author} - 韩漫免费阅读 - 韩漫网站`}
        description={`${comic.description} 好看的韩国漫画《${comic.title}》，作者${comic.author}，${comic.status}，评分${comic.rating}分。韩漫免费阅读，下拉式浏览体验。`}
        keywords={`${comic.title},${comic.author},韩国漫画,韩漫,韩漫免费阅读,${comic.tags.join(',')}`}
        canonicalUrl={`https://www.hanman.site/comic/${comic.id}`}
        ogType="article"
      />
      <SchemaOrg schema={articleSchema} />

      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: '分类', to: '/category/all' },
          { label: comic.title },
        ]}
      />

      <article className="w-full py-10 md:py-14 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
            <div className="flex-shrink-0 w-full sm:w-[280px] lg:w-[320px] mx-auto lg:mx-0">
              <div className="rounded-lg overflow-hidden aspect-[3/4]">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-4">
                {comic.title}
              </h1>

              <div className="flex items-center gap-3 flex-wrap mb-4">
                <span className="text-sm text-foreground-600">作者：<strong>{comic.author}</strong></span>
                <span className="text-sm text-foreground-400">|</span>
                <span className={`text-sm font-medium ${comic.status === '已完结' ? 'text-secondary-600' : 'text-primary-500'}`}>
                  {comic.status}
                </span>
                <span className="text-sm text-foreground-400">|</span>
                <span className="text-sm text-foreground-600 flex items-center gap-1">
                  <i className="ri-star-fill text-accent-500"></i>
                  <strong>{comic.rating}</strong> 分
                </span>
                <span className="text-sm text-foreground-400">|</span>
                <span className="text-sm text-foreground-600 flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {(comic.views / 10000).toFixed(1)}万 阅读
                </span>
              </div>

              <div className="flex items-center gap-2 flex-wrap mb-5">
                {comic.tags.map((tag) => (
                  <span key={tag} className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 font-medium">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="bg-background-100 rounded-lg p-4 md:p-5 mb-6">
                <h2 className="text-sm font-semibold text-foreground-900 mb-2">作品简介</h2>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {comic.description}
                </p>
              </div>

              <Link
                to={`/comic/${comic.id}/read`}
                className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-white rounded-full font-medium text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all duration-300"
              >
                <i className="ri-play-circle-line"></i> 开始阅读
              </Link>
            </div>
          </div>

          <div className="mt-12 md:mt-16">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
              章节列表
              <span className="text-sm font-normal text-foreground-500 ml-2">共 {comic.chapters.length} 话</span>
            </h2>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {comic.chapters.map((chapter) => (
                <Link
                  key={chapter.num}
                  to={`/comic/${comic.id}/read`}
                  className="flex items-center justify-between p-3 rounded-lg bg-background-100 hover:bg-primary-50 transition-colors cursor-pointer group"
                >
                  <span className="text-sm text-foreground-800 group-hover:text-primary-600 transition-colors">
                    {chapter.title}
                  </span>
                  <span className="text-xs text-foreground-400 flex-shrink-0 ml-2">{chapter.updatedAt}</span>
                </Link>
              ))}
            </div>
          </div>

          <div className="mt-10 pt-6 border-t border-background-200">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <Link
                to="/category/all"
                className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i> 返回漫画列表
              </Link>
              <div className="flex items-center gap-4">
                <button className="w-9 h-9 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-heart-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-share-forward-line"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </article>
    </Layout>
  );
}